import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import classNames from 'classnames';
import { ProgressSpinner } from 'primereact/progressspinner';

import Data from '../../data';
import { WrappedDropdown, WrappedGroupedDropdown } from '../input';
import WrappedTooltip from '../overlay/WrappedTooltip.component';
import LabelWrapper from '../LabelWrapper.component';

const { ServiceType, PackageType } = Data;
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

export class ServiceField extends Component {
	constructor(props) {
		super(props);

		// group services by "serviceType" prop value
		this.groupOptionValue = 'serviceType';

		// display "serviceName" prop value for the group option
		this.groupOptionLabel = 'serviceName';

		this.state = {
			serviceOptions: this.buildServiceOptions(props),
		};
	}

	componentDidUpdate(prevProps) {
		const { options } = this.props;

		if (options !== prevProps.options) {
			this.updateServiceOptions();
		}
	}

	/**
	 * Return an array of processed service options. The process is to strip out template literals,
	 * and replace them with HTML symbol entity, i.e. ®.
	 * @param {object} params
	 * @returns {array}
	 */
	buildServiceOptions = ({ showServicesOnly, options }) => {
		const { optionLabel, optionValue } = this.props;

		return reduce(
			options,
			(_serviceOptions, option) => {
				const _serviceOption = {
					...option,
					serviceName: isString(option.serviceName)
						? option.serviceName.replace('<sup>&reg;</sup>', '®')
						: null,
					packageName: isString(option.packageName)
						? option.packageName.replace('<sup>&reg;</sup>', '®')
						: null,
					selectedOptionValue: option[optionValue] ? option[optionValue] : option.value,
				};

				if (showServicesOnly) {
					if (
						!find(_serviceOptions, [
							this.groupOptionValue,
							_serviceOption[this.groupOptionValue],
						])
					) {
						_serviceOption.selectedOptionLabel = _serviceOption[optionLabel]
							? _serviceOption[optionLabel]
							: _serviceOption[this.groupOptionLabel];

						_serviceOptions.push(_serviceOption);
					}
				} else {
					/**
					 * Show the selected service string with the following requirements from Products:
					 * 1. Take the display service name. Ignore the first word (which will always be the carrier name)
					 * 2. Display the package name.
					 * 3. Display the ETA in ( ).
					 */
					let serviceOptionName;
					const isReturnTypeAvailable =
						option.serviceType === ServiceType.UspsPayOnUseReturn ||
						option.serviceType === ServiceType.UpsGround;

					if (!isReturnTypeAvailable && !_serviceOption.isStaticRate) {
						serviceOptionName = _serviceOption.serviceName
							.split(' ')
							.splice(1)
							.map(word => word[0].toUpperCase())
							.join('');
					} else if (isReturnTypeAvailable) {
						serviceOptionName = _serviceOption.serviceName.concat(' -');
					} else if (!isReturnTypeAvailable && _serviceOption.isStaticRate) {
						serviceOptionName = _serviceOption.serviceTitle.concat(' -');
					}

					_serviceOption.selectedOptionLabel = [
						// service name
						serviceOptionName,

						// package name
						_serviceOption.packageName,

						// eta
						this.buildETA(_serviceOption.minDeliverDays, _serviceOption.maxDeliverDays),
					].join(' ');

					_serviceOptions.push(_serviceOption);
				}

				return _serviceOptions;
			},
			[]
		);
	};

	/**
	 * Return the icon classname selector based on the service type and package type.
	 * @param {ServiceType} serviceType
	 * @param {PackageType} packageType
	 * @returns {string}
	 */
	buildPackageIconCls = (serviceType, packageType) => {
		const serviceTypesLookup = [
			ServiceType.UspsPriorityMailExpress,
			ServiceType.UspsPriorityMailExpressIntl,
		];
		const packageTypesLookup = [
			PackageType.UspsFlatRateEnvelope,
			PackageType.UspsLegalFlatRateEnvelope,
			PackageType.UspsPaddedFlatRateEnvelope,
		];
		const priorityMailExpressAppendix =
			includes(serviceTypesLookup, serviceType) && includes(packageTypesLookup, packageType)
				? '-pme'
				: '';

		if (isString(packageType)) {
			return packageType.toLowerCase().replace(/_/g, '-') + priorityMailExpressAppendix;
		} else {
			return null;
		}
	};

	/**
	 * Render estimated delivery days text depending on rate object. Server could return
	 * null, or "{x}", or "{x}-{y}". This method will render "(Varies)", or "({x} Day(s))"
	 * or "({x}-{y} Days)", respectively.
	 * See processRates method in RateAdvisorUtil class.
	 * @param {number|null} minDeliverDays
	 * @param {number|null} maxDeliverDays
	 * @returns {string|ReactElement}
	 */
	buildETA = (minDeliverDays, maxDeliverDays) => {
		if (!isNumber(minDeliverDays) && !isNumber(maxDeliverDays)) {
			return '';
		}

		// server return null => [1,999]
		if (minDeliverDays === 1 && maxDeliverDays === 999) {
			return 'Varies';
		}

		if (!isNumber(maxDeliverDays)) {
			return `(${minDeliverDays} ${minDeliverDays > 1 ? 'Days' : 'Day'})`;
		}

		return `(${minDeliverDays}-${maxDeliverDays} Days)`;
	};

	/**
	 * Return the template literal for the tooltip content of service group item.
	 * @returns {string}
	 */
	buildServiceGroupItemTooltip = (params, eta) => {
		const { isFetching, serviceName, serviceTypeDescription } = params;

		return ReactDOMServer.renderToString(
			<div className="common-ui-service-field-tooltip">
				<div className="title">
					{serviceName} {isFetching === true ? '' : eta}
				</div>
				<div
					className="description"
					// eslint-disable-next-line
					dangerouslySetInnerHTML={{ __html: serviceTypeDescription }}
				/>
			</div>
		);
	};

	/**
	 * Return the template literal for the tooltip content of service item.
	 * @returns {string}
	 */
	buildServiceItemTooltip = params => {
		const {
			serviceName,
			packageName,
			packageType,
			packageDescription,
			packageRequirements,
			isStaticRate,
		} = params;

		let title = 'Packaging Requirements:';

		if (packageType === PackageType.Postcard) {
			title = 'Postcard Requirements:';
		} else if (
			packageType === PackageType.Letter ||
			packageType === PackageType.LargeEnvelope
		) {
			title = 'Envelope Requirements';
		}

		return ReactDOMServer.renderToString(
			<div className="common-ui-service-field-tooltip">
				{!isStaticRate && (
					<div className="title">
						{serviceName} {packageName}
					</div>
				)}
				{isString(packageDescription) && (
					<div
						className="description"
						// eslint-disable-next-line
						dangerouslySetInnerHTML={{ __html: packageDescription }}
					/>
				)}
				{isArray(packageRequirements) && (
					<div className="description">
						<h4 className="title">{title}</h4>
						{packageRequirements.map((requirement, index) => (
							/* eslint-disable-next-line */
							<p key={index} dangerouslySetInnerHTML={{ __html: requirement }} />
						))}
					</div>
				)}
			</div>
		);
	};

	/**
	 * Notify the caller about the service selection.
	 * @param {object} data The service value and selection data.
	 */
	notifyParent = data => {
		const { onChange } = this.props;

		if (isFunction(onChange)) {
			onChange(data);
		}
	};

	/**
	 * Rebuild the service options when props udpate.
	 */
	updateServiceOptions = () => {
		const { value } = this.props;
		const serviceOptions = this.buildServiceOptions(this.props);
		const selectedServiceOption = find(serviceOptions, ['value', value]);

		this.setState(
			{
				serviceOptions,
			},
			() => {
				/**
				 * Dev note:
				 * Notify the caller about the selected service value and option data.
				 * If the selected service becomes no longer available from the rates set,
				 * we should reset the service to null.
				 */
				this.notifyParent({
					value: isObject(selectedServiceOption) ? selectedServiceOption.value : null,
					service: selectedServiceOption,
				});
			}
		);
	};

	onChange = ({ target }) => {
		const { value } = target;
		const { options } = this.props;
		const service = find(options, ['value', value]);

		this.notifyParent({
			value,
			service,
		});
	};

	/**
	 * Render service group item template: "<service name> (<ETA>)".
	 * @param {object}
	 * @returns {ReactElement}
	 */
	renderGroupItemTemplate = params => {
		const { groupItemTemplate } = this.props;
		const {
			serviceName,
			isFetching,
			minDeliverDays,
			maxDeliverDays,
			serviceType,
			packageType,
		} = params;
		const serviceEta = this.buildETA(minDeliverDays, maxDeliverDays);
		const serviceTooltip = this.buildServiceGroupItemTooltip(params, serviceEta);
		const tooltipTarget = `service-group-item-${serviceType}-${packageType}-tooltip`;

		if (groupItemTemplate) {
			return groupItemTemplate({ ...params, serviceEta, serviceTooltip });
		}

		return (
			<>
				<div className={classNames('service-group-item', tooltipTarget)}>
					<div className="service-title table-cell">
						{/* show the spinner while system is making a call to fetch ETA */}
						{isFetching === true ? (
							<>
								<span>{serviceName}</span>
								<ProgressSpinner strokeWidth="2" animationDuration=".7s" />
							</>
						) : (
							<span>
								{serviceName} {serviceEta}
							</span>
						)}
					</div>
				</div>
				<WrappedTooltip tooltip={serviceTooltip} target={tooltipTarget} />
			</>
		);
	};

	/**
	 * Render service item template: "<service icon> <package name> <service amount>".
	 * @param {object} params
	 * @returns {ReactElement}
	 */
	renderItemTemplate = params => {
		const { rateOptionValue, showServicesOnly, itemTemplate, optionLabel } = this.props;

		if (itemTemplate) {
			return itemTemplate(params);
		}

		if (showServicesOnly) {
			return (
				<div className="service-item">
					<div className="service-title table-cell">
						<span>{get(params, optionLabel)}</span>
					</div>
				</div>
			);
		}

		const { serviceType, packageType, packageName, isStaticRate } = params;
		const packageTypeIcon = this.buildPackageIconCls(serviceType, packageType);
		const tooltip = this.buildServiceItemTooltip(params);
		const amount = get(params, rateOptionValue);
		const tooltipTarget = `${serviceType}-${packageType}-tooltip`;

		return (
			<>
				<div className={classNames('service-item', tooltipTarget)}>
					<div className="service-icon table-cell">
						{!isStaticRate && <span className={`${serviceType} ${packageTypeIcon}`} />}
					</div>
					<div className="service-title table-cell ellipsis">
						<span>{packageName}</span>
					</div>
					{isNumber(amount) ? (
						<div className="service-cost table-cell">
							<span>{usCurrencyFormatter.format(amount)}</span>
						</div>
					) : null}
				</div>
				<WrappedTooltip tooltip={tooltip} target={tooltipTarget} />
			</>
		);
	};

	render() {
		const { value, enableGrouping, ...restProps } = this.props;
		const { serviceOptions } = this.state;

		return (
			<div className="common-ui-service-field">
				{enableGrouping ? (
					<WrappedGroupedDropdown
						{...restProps}
						value={value}
						options={serviceOptions}
						enableGrouping={enableGrouping}
						optionLabel="selectedOptionLabel"
						optionValue="selectedOptionValue"
						groupOptionLabel={this.groupOptionLabel}
						groupOptionValue={this.groupOptionValue}
						panelClassName="service-dropdown-panel carrier-dropdown-panel"
						itemTemplate={this.renderItemTemplate}
						groupItemTemplate={this.renderGroupItemTemplate}
						onChange={this.onChange}
					/>
				) : (
					<WrappedDropdown
						{...restProps}
						value={value}
						options={serviceOptions}
						optionLabel="selectedOptionLabel"
						optionValue="selectedOptionValue"
						panelClassName="service-dropdown-panel carrier-dropdown-panel"
						itemTemplate={this.renderItemTemplate}
						onChange={this.onChange}
					/>
				)}
			</div>
		);
	}
}

ServiceField.defaultProps = {
	showServicesOnly: false,
	optionValue: 'value',
	optionLabel: 'label',
	rateOptionValue: 'amount',
};

ServiceField.propTypes = {
	/**
	 * An array of services to be populated in the dropdown. This component
	 * is designed to work with services in Data module, so the each option
	 * object should contain properties defined in AllServices.js.
	 */
	options: PropTypes.array,

	// `true` to show service name only; package names will not be populated
	showServicesOnly: PropTypes.bool,

	// `true` to group the options
	enableGrouping: PropTypes.bool,

	/**
	 * Name of the service rate of an option.
	 * Use the dot notation if the rate is in a nested object.
	 */
	rateOptionValue: PropTypes.string,

	// Name of the label field of an option
	optionLabel: PropTypes.string,

	// Name of the value field of an option
	optionValue: PropTypes.string,

	// Value of the component
	value: PropTypes.any,

	// The template of an option group item
	groupItemTemplate: PropTypes.elementType,

	// The template of an option item
	itemTemplate: PropTypes.elementType,

	// The handler to handle selection change
	onChange: PropTypes.func.isRequired,
};

export const ServiceFieldWithLabel = LabelWrapper(ServiceField, 'common-ui-service-field-wrapper');
